// ============================================================
//  User Submissions — Microsoft Defender user-reported submissions
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaInbox,
  FaSearch,
  FaSpinner,
  FaSyncAlt,
  FaTimes,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import EmailAnalysisResult from "../PhishingAnalysis/EmailAnalysisResult";
import "../PhishingAnalysis/PhishingAnalysis.css";
import "./DefenderSubmissions.css";

const verdictTextClass = (verdict = "") => {
  const v = String(verdict || "").toLowerCase();
  if (!v) return "vtext-muted";
  if (v.includes("whitelist")) return "vtext-ok";
  if (v.includes("potentially")) return "vtext-warn";
  if (v.includes("malicious") || v.includes("phish")) return "vtext-danger";
  if (v.includes("suspicious") || v.includes("spam")) return "vtext-warn";
  if (v.includes("clean") || v.includes("safe")) return "vtext-ok";
  return "vtext-muted";
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
};

const EMPTY_FILTERS = { q: "", status: "", verdict: "" };

export default function DefenderSubmissions() {
  const { token } = useAuth();

  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [syncing, setSyncing]     = useState(false);
  const [syncMsg, setSyncMsg]     = useState("");

  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]         = useState(0);
  const [jumpInput, setJumpInput] = useState("");

  // Committed filters (what's actually been sent to the API)
  const [filters, setFilters]     = useState(EMPTY_FILTERS);
  // Draft text input — only applied on Enter / click Search
  const [searchDraft, setSearchDraft] = useState("");

  const [view, setView]           = useState(null);
  const [busyId, setBusyId]       = useState(null);

  const searchInputRef = useRef(null);

  const authHeaders = useCallback(
    (extra = {}) => ({ Authorization: `Bearer ${token}`, ...extra }),
    [token]
  );

  const buildQuery = (targetPage, f) => {
    const p = new URLSearchParams({ page: targetPage });
    if (f.q)       p.set("q",       f.q);
    if (f.status)  p.set("status",  f.status);
    if (f.verdict) p.set("verdict", f.verdict);
    return p.toString();
  };

  const loadList = useCallback(async (targetPage, f) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/defender-submissions?${buildQuery(targetPage, f)}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRows(json.submissions || []);
      setTotal(json.total ?? 0);
      setTotalPages(json.totalPages ?? 1);
      setPage(json.page ?? targetPage);
    } catch (e) {
      setError(e.message || "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadList(1, EMPTY_FILTERS); }, [loadList]);

  // Apply the text search draft
  const applySearch = () => {
    const newFilters = { ...filters, q: searchDraft.trim() };
    setFilters(newFilters);
    loadList(1, newFilters);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") applySearch();
  };

  const handleDropdownChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    loadList(1, newFilters);
  };

  const hasActiveFilters = filters.q || filters.status || filters.verdict;

  const clearFilters = () => {
    setSearchDraft("");
    setFilters(EMPTY_FILTERS);
    loadList(1, EMPTY_FILTERS);
    searchInputRef.current?.focus();
  };

  const goToPage = (p) => {
    const clamped = Math.max(1, Math.min(p, totalPages));
    loadList(clamped, filters);
  };

  const handleJump = (e) => {
    e.preventDefault();
    const n = parseInt(jumpInput, 10);
    if (!isNaN(n)) goToPage(n);
    setJumpInput("");
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg("");
    try {
      const res = await fetch("/api/defender-submissions/sync", {
        method: "POST",
        headers: authHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setSyncMsg(
        json.skipped
          ? `Sync skipped: ${json.reason}`
          : `Synced — scanned ${json.scanned ?? 0}, ingested ${json.ingested ?? 0}.`
      );
      await loadList(1, filters);
    } catch (e) {
      setSyncMsg(`Sync failed: ${e.message}`);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(""), 6000);
    }
  };

  const fetchRawFile = async (id, fileName) => {
    try {
      const res = await fetch(`/api/defender-submissions/${id}/raw`, { headers: authHeaders() });
      if (!res.ok) return null;
      const blob = await res.blob();
      return new File([blob], fileName || "reported.eml", { type: "message/rfc822" });
    } catch { return null; }
  };

  const handleAnalyze = async (row) => {
    setBusyId(row.id);
    setError("");
    try {
      const res = await fetch(`/api/defender-submissions/${row.id}/analyze`, {
        method: "POST",
        headers: authHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      const file = await fetchRawFile(row.id, json.result?.fileName);
      setView({ result: json.result, file, id: row.id });
      loadList(page, filters);
    } catch (e) {
      setError(e.message || "Analysis failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleView = async (row) => {
    setBusyId(row.id);
    setError("");
    try {
      const res = await fetch(`/api/defender-submissions/${row.id}`, { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      const stored = json.submission?.analysis_result;
      if (!stored) return handleAnalyze(row);
      const file = await fetchRawFile(row.id, stored.fileName);
      setView({ result: stored, file, id: row.id });
    } catch (e) {
      setError(e.message || "Failed to load result");
    } finally {
      setBusyId(null);
    }
  };

  /* ---------------- Inline result view ---------------- */
  if (view) {
    return (
      <div className="phish-page">
        <section className="phish-shell">
          <button type="button" className="bulk-back-btn" onClick={() => setView(null)}>
            <FaArrowLeft /> Back to submissions
          </button>
          <EmailAnalysisResult
            result={view.result}
            file={view.file}
            aiEnabled={!!view.result?.aiAnalysis}
            onClear={() => setView(null)}
          />
        </section>
      </div>
    );
  }

  const firstItem = total === 0 ? 0 : (page - 1) * 50 + 1;
  const lastItem  = Math.min(page * 50, total);

  /* ---------------- List view ---------------- */
  return (
    <div className="phish-page">
      <section className="phish-shell">

        {/* Header card */}
        <div className="card upload-card">
          <div className="upload-header">
            <div>
              <h2 className="section-title">
                <FaInbox style={{ marginRight: 8 }} />
                User Submissions
              </h2>
              <p className="section-desc">
                Emails your users reported through Microsoft Defender land here automatically. Click{" "}
                <strong>Analyze</strong> to run the full phishing analysis on the original message.
              </p>
            </div>
            <div className="defender-actions">
              <button
                type="button"
                className="defender-sync-btn"
                onClick={handleSync}
                disabled={syncing}
                title="Pull the latest reported emails from the Defender mailbox now"
              >
                {syncing ? <FaSpinner className="spin" /> : <FaSyncAlt />}
                {syncing ? " Syncing…" : " Sync now"}
              </button>
            </div>
          </div>
          {syncMsg && <div className="defender-sync-msg">{syncMsg}</div>}
          {error   && <div className="error-banner">{error}</div>}
        </div>

        {/* Search / filter bar */}
        <div className="card defender-filter-bar">
          <div className="defender-search-row">
            {/* Text search */}
            <div className="def-search-wrap">
              <FaSearch className="def-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="def-search-input"
                placeholder="Search subject, sender, recipient, analyzed by…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              {searchDraft && (
                <button
                  className="def-search-clear-x"
                  onClick={() => { setSearchDraft(""); if (filters.q) { const f = { ...filters, q: "" }; setFilters(f); loadList(1, f); } }}
                  title="Clear text"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <button className="def-search-btn" onClick={applySearch}>
              Search
            </button>

            {/* Status dropdown */}
            <select
              className="def-filter-select"
              value={filters.status}
              onChange={(e) => handleDropdownChange("status", e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="new">New</option>
              <option value="analyzed">Analyzed</option>
            </select>

            {/* Verdict dropdown */}
            <select
              className="def-filter-select"
              value={filters.verdict}
              onChange={(e) => handleDropdownChange("verdict", e.target.value)}
            >
              <option value="">All verdicts</option>
              <option value="phishing">Phishing</option>
              <option value="malicious">Malicious</option>
              <option value="suspicious">Suspicious</option>
              <option value="spam">Spam</option>
              <option value="safe">Safe</option>
              <option value="clean">Clean</option>
              <option value="needs review">Needs Review</option>
            </select>

            {hasActiveFilters && (
              <button className="def-clear-filters-btn" onClick={clearFilters} title="Clear all filters">
                <FaTimes /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Table card */}
        <div className="card defender-list-card">
          {loading ? (
            <p className="placeholder">
              <FaSpinner className="spin" /> Loading submissions…
            </p>
          ) : rows.length === 0 ? (
            <p className="placeholder">
              {hasActiveFilters
                ? "No submissions match your filters."
                : "No user submissions in the last 30 days. Once users report messages in Defender and a sync runs, they appear here."}
            </p>
          ) : (
            <>
              <div className="table-wrapper">
                <table className="table defender-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Sender</th>
                      <th>Reported</th>
                      <th>Status</th>
                      <th>Verdict</th>
                      <th>Analyzed by</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="defender-subject">{r.subject || "(no subject)"}</td>
                        <td className="mono mono-wrap">{r.sender || "—"}</td>
                        <td>{fmtDate(r.reported_at || r.created_at)}</td>
                        <td>
                          <span className={`status-text ${r.status === "analyzed" ? "status-analyzed" : "status-new"}`}>
                            {r.status === "analyzed" ? "Analyzed" : "New"}
                          </span>
                        </td>
                        <td>
                          {r.final_verdict || r.verdict ? (
                            <span className={`verdict-text ${verdictTextClass(r.final_verdict || r.verdict)}`}>
                              {r.final_verdict || r.verdict}
                            </span>
                          ) : (
                            <span className="verdict-text vtext-muted">—</span>
                          )}
                        </td>
                        <td>
                          {r.analyzed_by ? (
                            <div className="defender-analyzed-by">
                              <span className="analyzed-by-name">{r.analyzed_by}</span>
                              {r.analyzed_at && (
                                <span className="analyzed-by-time">{fmtDate(r.analyzed_at)}</span>
                              )}
                            </div>
                          ) : (
                            <span className="verdict-text vtext-muted">—</span>
                          )}
                        </td>
                        <td>
                          <div className="defender-row-actions">
                            <button
                              type="button"
                              className="defender-analyze-btn"
                              onClick={() => handleAnalyze(r)}
                              disabled={busyId === r.id}
                            >
                              {busyId === r.id ? <FaSpinner className="spin" /> : <FaSearch />}
                              {busyId === r.id ? " Analyzing…" : r.status === "analyzed" ? " Re-analyze" : " Analyze"}
                            </button>
                            {r.status === "analyzed" && (
                              <button
                                type="button"
                                className="defender-view-btn"
                                onClick={() => handleView(r)}
                                disabled={busyId === r.id}
                              >
                                View
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination bar */}
              <div className="defender-pagination">
                <span className="defender-pagination-info">
                  {firstItem}–{lastItem} of {total.toLocaleString()} email{total !== 1 ? "s" : ""}
                  {hasActiveFilters ? " matching filters" : " (last 30 days)"}
                </span>
                <div className="defender-pagination-controls">
                  <button className="def-pg-btn" onClick={() => goToPage(page - 1)} disabled={page <= 1} title="Previous page">
                    <FaChevronLeft />
                  </button>
                  <span className="def-pg-label">Page {page} of {totalPages}</span>
                  <button className="def-pg-btn" onClick={() => goToPage(page + 1)} disabled={page >= totalPages} title="Next page">
                    <FaChevronRight />
                  </button>
                  <form className="def-pg-jump" onSubmit={handleJump}>
                    <input
                      type="number"
                      className="def-pg-input"
                      min={1}
                      max={totalPages}
                      placeholder="Go to…"
                      value={jumpInput}
                      onChange={(e) => setJumpInput(e.target.value)}
                    />
                    <button type="submit" className="def-pg-go">Go</button>
                  </form>
                </div>
              </div>
            </>
          )}
        </div>

      </section>
    </div>
  );
}
